import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker, uiMarkerInternal, uiMarkerLayout } from './shared';

/**
 * APP SHELL
 *
 * The frame every signed-in screen renders inside, as the dashboard's Figma
 * draws it (17:2492): the sidebar and a row of breadcrumbs on a pale ground,
 * and the page in a white card beside them. The shell is the height of the
 * viewport and never scrolls itself — only the card does — so the sidebar
 * and the breadcrumbs hold still while a long table scrolls under them.
 *
 * The shell restates `font-sans` and `text-foreground` rather than leave
 * them to <body>, so a page that sets its own face or colour on <body>
 * (the sales pages do) cannot reach into the frame.
 *
 * The header and the main are landmarks: a screen reader user jumps to
 * `banner` and `main` by name, so they are real elements rather than divs
 * with roles.
 */
export const appShellRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex h-screen w-full items-stretch overflow-hidden bg-canvas font-sans text-foreground',
]);

/** The column beside the sidebar: the top bar, the page, and what floats over them. */
export const appShellColumnRecipe = cva([
  uiMarkerInternal,
  'flex min-w-0 grow flex-col overflow-hidden',
]);

/** Header over main, sharing the column's height between them. */
export const appShellBodyRecipe = cva([
  uiMarkerInternal,
  'flex min-h-0 grow flex-col overflow-x-hidden',
]);

/**
 * The breadcrumb row along the top, level with the card's edges: 40 high,
 * so the crumbs sit 8px down as the Figma has them and the card starts at
 * 40. Flush with the card on the left, where the sidebar's own inset is the
 * gap; on a phone there is no sidebar, so it takes the card's inset.
 */
export const appShellHeaderRecipe = cva([
  uiMarker,
  'flex h-10 shrink-0 items-center justify-between gap-4 px-3 md:pl-0',
]);

/*
 * The content card, which is what scrolls. It carries no inset of its own:
 * a pinned element inside a padded scroller stops short of the top by the
 * padding, and content shows in the strip above it. The inset sits on the
 * page box inside it instead. The overflow clips the page to the card's
 * corners.
 *
 * Its ground is `background`, so a page that paints `bg-background`
 * matches the card it is in, in either mode. The hairline is the foreground
 * at 5%, the Figma's black at 5%, which stays a hairline in the dark too.
 */
export const appShellMainRecipe = cva([
  uiMarker,
  'mx-3 mb-3 min-h-0 w-auto max-w-full flex-1 overflow-y-auto overflow-x-hidden md:ml-0',
  'rounded-lg border border-foreground/5 bg-background font-sans text-foreground shadow-page',
]);

/**
 * The box the page fills. Full height, so a page that sizes itself to the
 * shell (the inspections tab, whose columns scroll on their own) still has a
 * height to fill.
 */
export const appShellPageRecipe = cva([uiMarker, 'h-full'], {
  variants: {
    /**
     * The page's inset: 16 at the sides and foot and 12 at the top, as the
     * dashboard's Figma (17:2492) starts the page's header and its columns.
     * It was 32, double that, which is why list pages took half of it back
     * (`bleed`). `none` for a screen that draws edge to edge — the studio.
     */
    padding: {
      none: '',
      md: 'px-4 pb-4 pt-3',
    },
  },
  defaultVariants: { padding: 'md' },
});

export type AppShellPageRecipeProps = VariantProps<typeof appShellPageRecipe>;
